import { FC, useEffect, useRef } from 'react';
import * as React from 'react';
import cn from 'classnames';
import { Content } from '@raydiant/api-client-js';
import Text from '@raydiant/raydial/components/Text';
import { Check, User } from '@raydiant/raydial/components/Icons';
import { titleCase } from '../../../../../utilities/stringUtils';
import { LibraryPageModalType } from '../../../../../pages/LibraryPage/v2/LibraryPageV2';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';
import Spinner from '@raydiant/raydial/components/Spinner';
import ApplicationIcon from '../ApplicationIcon';
import {
  getPresentationData,
  DEFAULT_SHARED_LIBRARY_FOLDER_DATA,
} from '../../../utils';
import SidebarMenuTooltip from '../../../../../components/Sidebar/SidebarMenuTooltip';
import ContentCardActionButtonGroup from './ContentCardActionButtonGroup';
import { useSelector } from 'react-redux';
import { selectLocalUploads } from '../../../../../selectors/fileUploads';
import { getContentFileStatus } from '../../../../../utilities/presentationUtils';
import UploadErrorIcon from '../../../../../components/UploadErrorIcon';

interface ContentResourceCardProps {
  content: Content;
  showOwner?: boolean;
  ownerLabel?: string;
  hideMoveOption?: boolean;
  libraryQuery?: typeof DEFAULT_QUERY;
  selected?: boolean;
  // "See in Library" target: same border as `selected` but no checkmark, and
  // scrolls itself into view on mount.
  highlighted?: boolean;
  batchMode?: boolean;
  isTopLevelSharedLibraryFolder?: boolean;
  hideShare?: boolean;
  onClick: (content: Content) => void;
  onSelect: (content: Content) => void;
  onButtonGroupClick: (contentId: string) => void;
  onButtonGroupItemClick: (modalType: LibraryPageModalType) => void;
  onClose: () => void;
  onCreateNewPlaylist?: (content: Content) => void;
  onAddToPlaylist?: (content: Content) => void;
  onDuplicateFolder?: (content: Content) => void;
  onDuplicateContent?: (content: Content) => void;
  showDuplicateAndMove?: boolean;
}

const LibraryContentCard: FC<ContentResourceCardProps> = (
  {
    content,
    showOwner = false,
    ownerLabel = 'Owner',
    hideMoveOption = false,
    libraryQuery = DEFAULT_QUERY,
    selected = false,
    highlighted = false,
    batchMode = false,
    isTopLevelSharedLibraryFolder = false,
    hideShare = false,
    onClick,
    onSelect,
    onButtonGroupClick,
    onButtonGroupItemClick,
    onClose,
    onCreateNewPlaylist,
    onAddToPlaylist,
    onDuplicateFolder,
    onDuplicateContent,
    showDuplicateAndMove = false,
  },
  ref,
) => {
  const nextContent = {
    ...content,
    presentationData:
      isTopLevelSharedLibraryFolder && content.resourceType === 'folder'
        ? DEFAULT_SHARED_LIBRARY_FOLDER_DATA
        : getPresentationData(content),
  };

  // Scroll the "See in Library" target into view once it renders highlighted.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlighted) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  const localUploads = useSelector(selectLocalUploads);
  const { isUploading: isFileUploading, hasError: hasUploadError } =
    getContentFileStatus(content, localUploads);
  const renderUploadStatusBadge = () => {
    if (isFileUploading) {
      return (
        <div
          className="absolute bottom-3 right-4 z-[2] flex items-center gap-1 rounded-md px-1.5 py-1 shadow-sm bg-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Spinner className="size-3 text-blue-600" />
          <span className="text-sm font-medium text-black">Uploading...</span>
        </div>
      );
    }
    if (hasUploadError) {
      return (
        <div
          className="absolute bottom-3 right-4 z-[2] flex items-center gap-1 rounded-md px-1.5 py-1 shadow-sm bg-red-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex size-4 shrink-0 items-center justify-center rounded-md">
            <UploadErrorIcon className="size-4 text-red-700" />
          </div>
          <span className="text-sm font-medium text-red-700">
            Upload Failed
          </span>
        </div>
      );
    }
    return null;
  };

  const renderShowOwnerInfo = () => {
    return (
      <SidebarMenuTooltip
        placement="bottom"
        title={`${ownerLabel}: ${titleCase(content.ownedBy.name)}`}
        arrow
      >
        <div
          className="bg-white w-6 h-6 rounded-xl flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <User size={15} className="mb-0.5" />
        </div>
      </SidebarMenuTooltip>
    );
  };

  const renderSelectStatus = (content: Content) => {
    return (
      <div
        className="bg-white w-6 h-6 rounded-xl flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(content);
        }}
      >
        {selected && <Check className="text-blue-600" />}
      </div>
    );
  };

  return (
    <div
      ref={cardRef}
      className={cn(
        'h-[185px] rounded-[20px] shadow border-2 border-grey-100 flex-col justify-start items-start inline-flex cursor-pointer',
        (selected || highlighted) && 'border-blue-600',
      )}
      onClick={() => onClick(content)}
    >
      {/* The ref should be applied on the thumbnail for correct drag and drop calculations */}
      <div
        className="relative w-[220px] h-[125px] p-4 rounded-t-[17px] flex justify-between items-start bg-center bg-no-repeat bg-cover"
        ref={ref}
      >
        <div
          ref={ref}
          className="absolute inset-0 rounded-t-[17px] bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${
              nextContent.presentationData.thumbnailUrl ||
              content.presentationData.applicationThumbnailUrl
            })`,
          }}
        />
        {hasUploadError && (
          <div
            className="absolute inset-0 z-[1] rounded-t-[17px] bg-black/50"
            aria-hidden="true"
          />
        )}
        <div className="z-[2]">
          {(batchMode || selected) && renderSelectStatus(content)}
          {!batchMode && showOwner && renderShowOwnerInfo()}
        </div>
        {renderUploadStatusBadge()}
        <div className="z-[2] flex items-center justify-end gap-1">
          <ContentCardActionButtonGroup
            content={content}
            onButtonGroupClick={onButtonGroupClick}
            onButtonGroupItemClick={onButtonGroupItemClick}
            onButtonGroupClose={onClose}
            onCreateNewPlaylist={onCreateNewPlaylist}
            onAddToPlaylist={onAddToPlaylist}
            onDuplicateFolder={onDuplicateFolder}
            onDuplicateContent={onDuplicateContent}
            hideMoveOption={hideMoveOption}
            libraryQuery={libraryQuery}
            isTopLevelSharedLibraryFolder={isTopLevelSharedLibraryFolder}
            hideShare={hideShare}
            showDuplicateAndMove={showDuplicateAndMove}
          />
        </div>
      </div>
      <div className="w-[220px] h-[60px] px-4 bg-white rounded-b-[20px] flex-col justify-center items-start gap-2.5 flex">
        <div className="self-stretch justify-start items-center gap-2 inline-flex">
          {hasUploadError ? (
            <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-red-50">
              <UploadErrorIcon className="size-5 text-red-700" />
            </div>
          ) : (
            <ApplicationIcon
              url={nextContent.presentationData.applicationIconUrl}
            />
          )}
          <div
            className={cn(
              'grow shrink basis-0 break-all line-clamp-2',
              hasUploadError ? 'text-gray-300' : 'text-gray-600',
            )}
          >
            <Text variant="body">{nextContent.name}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(LibraryContentCard);
